import { useEffect, useState } from "react";
import { useLang } from "../../core/lang";
import LangKeys from "../../core/locales/keys";
import { ArrowLeft } from "lucide-react";

function PremiumPlansPage({ onBack, onUpgradeSuccess }) {
  const { t } = useLang();

  const baseUrl = "https://khata.moxpal.com/api";

  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [premiumLevel, setPremiumLevel] = useState(0);
  const [premiumValidTill, setPremiumValidTill] = useState(null);
  const [premiumDaysLeft, setPremiumDaysLeft] = useState(null);
  const [activePlanId, setActivePlanId] = useState(97);

  const [selectedDurationMonths, setSelectedDurationMonths] = useState(1); // 1, 6, or 12
  const [plans, setPlans] = useState([]);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(-1);

  useEffect(() => {
    initLoad();
  }, []);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("login_token") ||
      ""
    );
  };

  const calculateDaysLeft = (validTill) => {
    if (validTill) {
      try {
        const expiry = new Date(validTill.replace(/-/g, "/"));
        const now = new Date();
        const diffMs = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr.replace(/-/g, "/"));
      const day = date.getDate();
      const monthIndex = date.getMonth() + 1; // 1-12
      const monthKey = `month_${monthIndex}`;
      const monthTranslated = monthKey ? t(LangKeys[monthKey]) : "";
      const year = date.getFullYear();
      return `${day} ${monthTranslated || date.toLocaleString('en-US', { month: 'short' })}, ${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const checkIsExpired = () => {
    if (isPremium) return false;
    if (!premiumValidTill) return false;
    try {
      const expiry = new Date(premiumValidTill.replace(/-/g, "/"));
      const now = new Date();
      return expiry.getTime() < now.getTime();
    } catch (e) {
      return false;
    }
  };
  const isSubscriptionExpired = checkIsExpired();

  const loadData = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${baseUrl}/v2/index.php?path=whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        const d = json.data;
        const premiumLevelVal = parseInt(d.is_premium || 0);
        const serverIsPremium = premiumLevelVal > 0;
        const serverValidTill = d.premium_valid_till;

        setIsPremium(serverIsPremium);
        setPremiumLevel(premiumLevelVal);
        setPremiumValidTill(serverValidTill);

        let calculatedPlanId = 97;
        if (serverIsPremium) {
          if (premiumLevelVal === 1) {
            calculatedPlanId = 96;
          } else if (premiumLevelVal === 2) {
            calculatedPlanId = 97;
          } else if (premiumLevelVal === 3) {
            calculatedPlanId = 98;
          }
          localStorage.setItem("premium_plan_id", calculatedPlanId.toString());
          setActivePlanId(calculatedPlanId);
        } else {
          const storedPlanId = localStorage.getItem("premium_plan_id");
          if (storedPlanId) {
            setActivePlanId(parseInt(storedPlanId));
          } else {
            setActivePlanId(97); // default fallback
          }
        }

        if (serverValidTill) {
          const days = calculateDaysLeft(serverValidTill);
          setPremiumDaysLeft(days);
        } else {
          setPremiumDaysLeft(null);
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getMockBenefitsForPlan = (id) => {
    const idStr = id.toString();
    if (idStr === "96") {
      return [
        "Unlimited Transactions",
        "No Ads",
      ];
    } else if (idStr === "97") {
      return [
        "Unlimited Transactions",
        "No Ads",
        "Multiple Devices",
        "Desktop Access",
        "Organize Lists",
        "Create Bills",
        "Defaulters List",
        "Priority Support",
        "Manage Stocks",
      ];
    } else {
      return [
        "All benefits of Ads Free+ Plan",
        "Dairy Management",
        "Refer & Earn",
        "Unlimited Transaction SMS",
      ];
    }
  };

  const getRawPlansForDuration = (months) => {
    return [
      {
        id: 96,
        name: "Standard",
        price: months === 12 ? 432 : months === 6 ? 216 : 36,
        offer_price: months === 12 ? 279 : months === 6 ? 149 : 29,
        duration:
          months === 12
            ? "365 days (1 Year)"
            : months === 6
            ? "180 days (6 Months)"
            : "30 days",
        duration_days: months === 12 ? 365 : months === 6 ? 180 : 30,
        discount_percent: months === 12 ? 35 : months === 6 ? 31 : 20,
        benefits: ["Unlimited Transactions", "No Ads"],
      },
      {
        id: 97,
        name: "Ads Free+",
        price: months === 12 ? 900 : months === 6 ? 450 : 75,
        offer_price: months === 12 ? 399 : months === 6 ? 229 : 45,
        duration:
          months === 12
            ? "365 days (1 Year)"
            : months === 6
            ? "180 days (6 Months)"
            : "30 days",
        duration_days: months === 12 ? 365 : months === 6 ? 180 : 30,
        discount_percent: months === 12 ? 56 : months === 6 ? 49 : 40,
        tag: months === 12 ? "Best Value Plan" : "Recommended Plan",
        tag_key:
          months === 12 ? "plan_best_value_plan_tag" : "plan_recommended_tag",
        benefits: [
          "Unlimited Transactions",
          "No Ads",
          "Multiple Devices",
          "Desktop Access",
          "Organize Lists",
          "Create Bills",
          "Defaulters List",
          "Priority Support",
          "Manage Stocks",
        ],
      },
      {
        id: 98,
        name: "Premium",
        price: months === 12 ? 3588 : months === 6 ? 1794 : 299,
        offer_price: months === 12 ? 799 : months === 6 ? 499 : 99,
        duration:
          months === 12
            ? "365 days (1 Year)"
            : months === 6
            ? "180 days (6 Months)"
            : "30 days",
        duration_days: months === 12 ? 365 : months === 6 ? 180 : 30,
        discount_percent: months === 12 ? 78 : months === 6 ? 72 : 67,
        tag:
          months === 12
            ? "Most Popular • Save ₹2,789"
            : months === 6
            ? "Save ₹1,295"
            : null,
        tag_key:
          months === 12
            ? "plan_most_popular_save"
            : months === 6
            ? "plan_save_amount_tag"
            : null,
        tag_param: months === 12 ? "2,789" : months === 6 ? "1,295" : null,
        benefits: [
          "All benefits of Ads Free+ Plan",
          "Dairy Management",
          "Refer & Earn",
          "Unlimited Transaction SMS",
        ],
      },
    ];
  };

  const recalculatePlansForDuration = (months, serverActivePlanId) => {
    const rawPlans = getRawPlansForDuration(months);
    const mapped = rawPlans.map((p) => {
      p.benefits = getMockBenefitsForPlan(p.id);
      return p;
    });
    setPlans(mapped);
    if (selectedPlanIndex === -1) {
      const activeIdx = mapped.findIndex((p) => p.id === serverActivePlanId);
      setSelectedPlanIndex(activeIdx !== -1 ? activeIdx : 1);
    }
  };

  const handleDurationChange = (months) => {
    if (selectedDurationMonths === months) return;
    setSelectedDurationMonths(months);
    recalculatePlansForDuration(months, activePlanId);
  };

  const initLoad = async () => {
    setLoading(true);
    let tempActivePlanId = 97;
    try {
      const token = getToken();
      const res = await fetch(`${baseUrl}/v2/index.php?path=whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        const d = json.data;
        const premiumLevelVal = parseInt(d.is_premium || 0);
        const tempIsPremium = premiumLevelVal > 0;
        if (tempIsPremium) {
          let calculatedPlanId = 97;
          if (premiumLevelVal === 1) {
            calculatedPlanId = 96;
          } else if (premiumLevelVal === 2) {
            calculatedPlanId = 97;
          } else if (premiumLevelVal === 3) {
            calculatedPlanId = 98;
          }
          tempActivePlanId = calculatedPlanId;
          localStorage.setItem("premium_plan_id", calculatedPlanId.toString());
        } else {
          const storedPlanId = localStorage.getItem("premium_plan_id");
          if (storedPlanId) {
            tempActivePlanId = parseInt(storedPlanId);
          } else {
            tempActivePlanId = 97;
          }
        }
      }
    } catch (e) {
      console.log(e);
    }

    await loadData();
    recalculatePlansForDuration(selectedDurationMonths, tempActivePlanId);
    setLoading(false);
  };

  const getLocalBenefit = (benefit) => {
    const text = benefit.toLowerCase();
    if (text.includes("dairy")) {
      return t(LangKeys.benefit_dairy_management) || "Smart Dairy Management (FAT/SNF)";
    }
    if (text.includes("refer") || text.includes("earn")) {
      return t(LangKeys.benefit_refer_earn) || "Refer & Earn Free Rewards";
    }
    if (text.includes("unlimited transactions")) {
      return t(LangKeys.benefit_unlimited_transactions) || "Unlimited Transactions";
    }
    if (text.includes("transaction sms") || text.includes("sms")) {
      return t(LangKeys.benefit_unlimited_sms) || "Unlimited transaction SMS";
    }
    if (text.includes("no ads") || text.includes("100%")) {
      return t(LangKeys.benefit_no_ads) || "Ad-free experience (100% Ads Free)";
    }
    if (text.includes("priority") || text.includes("support")) {
      return t(LangKeys.benefit_support) || "24/7 Priority Customer Support";
    }
    if (text.includes("all benefits") || text.includes("ads free++") || text.includes("ads free+")) {
      return t(LangKeys.benefit_all_ads_free) || "All benefits of Ads Free+ Plan";
    }
    if (text.includes("multiple") || text.includes("device")) {
      return t(LangKeys.benefit_multiple_devices) || "Multiple device login together";
    }
    if (text.includes("computer") || text.includes("desktop")) {
      return t(LangKeys.benefit_desktop_access) || "Access on computer/desktop";
    }
    if (text.includes("bill") || text.includes("gst")) {
      return t(LangKeys.benefit_create_bills) || "Create bills (Normal, GST Bills)";
    }
    if (text.includes("defaulter")) {
      return t(LangKeys.benefit_defaulters_list) || "View Defaulters list in one click";
    }
    if (text.includes("list")) {
      return t(LangKeys.benefit_organize_lists) || "Organize accounts with lists";
    }
    if (text.includes("stock") || text.includes("item")) {
      return t(LangKeys.benefit_manage_stocks) || "Manage stocks (items with transactions)";
    }
    if (text.includes("report") || text.includes("pdf")) {
      return t(LangKeys.benefit_financial_reports) || "Premium financial reports (PDF/Excel)";
    }
    if (text.includes("backup") || text.includes("cloud")) {
      return t(LangKeys.benefit_dual_backup) || "Secure local and cloud storage backup";
    }
    return benefit;
  };

  const getIconForBenefit = (benefit) => {
    const text = benefit.toLowerCase();
    if (text.includes("ad") || text.includes("ads")) return "🚫";
    if (text.includes("dairy")) return "🥛";
    if (text.includes("refer") || text.includes("earn") || text.includes("gift")) return "🎁";
    if (text.includes("backup") || text.includes("cloud")) return "☁️";
    if (text.includes("multiple") || text.includes("device")) return "📱";
    if (text.includes("report") || text.includes("pdf")) return "📄";
    if (text.includes("stock") || text.includes("item")) return "📦";
    if (text.includes("support") || text.includes("customer")) return "🎧";
    if (text.includes("transaction") || text.includes("unlimited")) return "🔄";
    if (text.includes("party") || text.includes("ledger") || text.includes("account")) return "👥";
    if (text.includes("bill") || text.includes("gst")) return "🧾";
    if (text.includes("defaulter")) return "👁️";
    if (text.includes("computer") || text.includes("desktop")) return "💻";
    if (text.includes("list")) return "📝";
    return "✅";
  };

  const startPayment = async () => {
    if (paying || selectedPlanIndex === -1 || plans.length === 0) return;
    const selectedPlan = plans[selectedPlanIndex];
    const durationDays =
      selectedDurationMonths === 12
        ? 365
        : selectedDurationMonths === 6
        ? 180
        : 30;

    try {
      setPaying(true);
      const token = getToken();
      const res = await fetch(`${baseUrl}/v2/index.php?path=razorpay/create-order`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          plan_id: selectedPlan.id.toString(),
          duration_months: selectedDurationMonths.toString(),
          duration_days: durationDays.toString(),
          amount: selectedPlan.offer_price.toString(),
        }),
      });
      const json = await res.json();
      if (!json.success) {
        alert(t(LangKeys.something_went_wrong) || "Order creation failed");
        setPaying(false);
        return;
      }

      const options = {
        key: "rzp_live_STAB6tst63SpHe",
        amount: json.amount,
        currency: "INR",
        name: t(LangKeys.my_subscription_title) || "Premium Plans",
        description: `${selectedPlan.name} - ${selectedPlan.duration}`,
        order_id: json.order_id,
        handler: async function (response) {
          await verifyPayment(response);
        },
        modal: {
          ondismiss: function () {
            setPaying(false);
          },
        },
        theme: { color: "#D97706" }, // Premium Gold Theme Color
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function () {
        alert("Payment failed. Please try again.");
        setPaying(false);
      });
      razorpay.open();
    } catch (e) {
      console.log(e);
      alert("Payment initialization failed.");
      setPaying(false);
    }
  };

  const verifyPayment = async (response) => {
    if (selectedPlanIndex === -1) return;
    const selectedPlan = plans[selectedPlanIndex];
    const durationDays =
      selectedDurationMonths === 12
        ? 365
        : selectedDurationMonths === 6
        ? 180
        : 30;

    try {
      const token = getToken();
      const verifyRes = await fetch(`${baseUrl}/v2/index.php?path=razorpay/verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          payment_id: response.razorpay_payment_id || "",
          order_id: response.razorpay_order_id || "",
          signature: response.razorpay_signature || "",
          plan_id: selectedPlan.id.toString(),
          duration_months: selectedDurationMonths.toString(),
          duration_days: durationDays.toString(),
        }),
      });
      const json = await verifyRes.json();
      if (json.success) {
        alert("Plan upgraded successfully! 🎉");
        localStorage.setItem("premium_plan_id", selectedPlan.id.toString());
        if (onUpgradeSuccess) {
          onUpgradeSuccess();
        }
        await initLoad();
      } else {
        alert("Payment verification failed.");
      }
    } catch (e) {
      console.log(e);
      alert("Error verifying payment.");
    }
    setPaying(false);
  };

  if (loading) {
    return (
      <div style={onBack ? styles.container : {}}>
        {onBack && (
          <div style={styles.appBar}>
            <button style={styles.backBtn} onClick={onBack}>
              <ArrowLeft size={20} color="#FFFFFF" />
            </button>
            <h3 style={styles.appBarTitle}>{t(LangKeys.premium_plans_title)}</h3>
          </div>
        )}
        <div style={styles.loaderWrap}>
          <div style={styles.loader}></div>
        </div>
      </div>
    );
  }

  const selectedPlan = plans[selectedPlanIndex];
  const currentDurationDays =
    selectedDurationMonths === 12
      ? 365
      : selectedDurationMonths === 6
      ? 180
      : 30;

  const content = (
    <div style={styles.page}>
      {/* Dynamic Subscription Banner */}
      {isPremium ? (
        <div style={styles.activeBanner}>
          <div style={styles.bannerDecor}>🥇</div>
          <div>
            <div style={styles.bannerTitle}>
              {t(LangKeys.my_subscription_title) || "My Subscription"}:{" "}
              <span style={styles.goldText}>
                {plans.find((p) => p.id === activePlanId)?.name || "Active"}
              </span>
            </div>
            <div style={styles.bannerSub}>
              {premiumValidTill && (
                <span>
                  {t(LangKeys.expires_on_label)?.replace("{date}", formatExpiryDate(premiumValidTill)) ||
                    `Expires on: ${formatExpiryDate(premiumValidTill)}`}
                </span>
              )}
              {premiumDaysLeft !== null && (
                <span style={styles.daysBadge}>
                  {t(LangKeys.days_remaining_label)?.replace("{days}", premiumDaysLeft) ||
                    `(${premiumDaysLeft} Days Left)`}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : isSubscriptionExpired ? (
        <div style={styles.expiredBanner}>
          <div style={styles.expiredBannerDecor}>❌</div>
          <div>
            <div style={styles.bannerTitle}>
              {t(LangKeys.my_subscription_title) || "My Subscription"}:{" "}
              <span style={styles.expiredText}>
                {plans.find((p) => p.id === activePlanId)?.name || "Expired"}
              </span>
            </div>
            <div style={styles.expiredOnText}>
              {premiumValidTill && (
                <span>
                  {t(LangKeys.expired_on_label)?.replace("{date}", formatExpiryDate(premiumValidTill)) ||
                    `Expired on: ${formatExpiryDate(premiumValidTill)}`}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.introBanner}>
          <div style={styles.introIcon}>👑</div>
          <div>
            <h3 style={styles.introTitle}>{t(LangKeys.premium_plans_title)}</h3>
            <p style={styles.introDesc}>
              {t(LangKeys.benefit_no_ads) || "Ad-free experience & premium business tools."}
            </p>
          </div>
        </div>
      )}

      {/* Duration Selector Tabs (1 Month / 6 Months / 12 Months) */}
      <div style={styles.durationSelectorWrap}>
        <div style={styles.durationSelector}>
          {[
            {
              months: 1,
              label: t(LangKeys.duration_1_month) || "1 Month",
              badge: t(LangKeys.plan_duration_30_days_badge) || "30 Days",
              icon: "📅",
            },
            {
              months: 6,
              label: t(LangKeys.duration_6_months) || "6 Months",
              badge: t(LangKeys.plan_save_20_percent) || "Save ~20%",
              icon: "💰",
            },
            {
              months: 12,
              label: t(LangKeys.duration_12_months) || "12 Months",
              badge: t(LangKeys.plan_best_value_badge) || "Best Value",
              icon: "👑",
              isBestseller: true,
            },
          ].map((tab) => {
            const isSelected = selectedDurationMonths === tab.months;
            return (
              <button
                key={tab.months}
                type="button"
                onClick={() => handleDurationChange(tab.months)}
                style={{
                  ...styles.durationTab,
                  ...(isSelected ? styles.durationTabActive : {}),
                }}
              >
                <span
                  style={
                    isSelected
                      ? styles.durationTabLabelActive
                      : styles.durationTabLabel
                  }
                >
                  {tab.label}
                </span>
                <span
                  style={{
                    ...styles.durationTabBadge,
                    ...(tab.isBestseller
                      ? styles.durationTabBadgeGold
                      : styles.durationTabBadgeGreen),
                  }}
                >
                  <span style={{ fontSize: "11px", marginRight: "3px" }}>
                    {tab.icon}
                  </span>
                  {tab.badge}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Plans Selection Cards */}
      <div style={styles.plansGrid}>
        {plans.map((plan, index) => {
          const isSelected = selectedPlanIndex === index;
          const isActive = isPremium && plan.id === activePlanId;
          const isExpired =
            !isPremium && isSubscriptionExpired && plan.id === activePlanId;

          let tagText = null;
          if (plan.tag_key) {
            if (plan.tag_key === "plan_most_popular_save") {
              tagText =
                t(LangKeys.plan_most_popular_save)?.replace(
                  "{amount}",
                  plan.tag_param || "2,789"
                ) || plan.tag;
            } else if (plan.tag_key === "plan_save_amount_tag") {
              tagText =
                t(LangKeys.plan_save_amount_tag)?.replace(
                  "{amount}",
                  plan.tag_param || "1,295"
                ) || plan.tag;
            } else if (plan.tag_key === "plan_best_value_plan_tag") {
              tagText =
                t(LangKeys.plan_best_value_plan_tag) || "Best Value Plan";
            } else if (plan.tag_key === "plan_recommended_tag") {
              tagText =
                t(LangKeys.plan_recommended_tag) || "Recommended";
            }
          } else if (plan.tag) {
            tagText = plan.tag;
          }

          return (
            <div
              key={plan.id}
              onClick={() => setSelectedPlanIndex(index)}
              style={{
                ...styles.planCard,
                ...(isSelected ? styles.selectedCard : {}),
                ...(isActive ? styles.activeCard : {}),
                ...(isExpired ? styles.expiredCard : {}),
              }}
            >
              {isActive ? (
                <div style={styles.activePlanBadge}>
                  {t(LangKeys.active_badge_label) || "Active"}
                </div>
              ) : isExpired ? (
                <div style={styles.expiredPlanBadge}>
                  {t(LangKeys.expired_badge_label) || "Expired"}
                </div>
              ) : tagText ? (
                <div
                  style={{
                    ...styles.recommendedBadge,
                    ...(plan.id === 98 ? styles.popularBadge : {}),
                  }}
                >
                  {tagText}
                </div>
              ) : null}

              <div style={styles.planHeader}>
                <span style={styles.planName}>{plan.name}</span>
                {plan.discount_percent && (
                  <span style={styles.discountBadge}>
                    {t(LangKeys.plan_save_text) || "Save"} {plan.discount_percent}%
                  </span>
                )}
              </div>

              <div style={styles.priceContainer}>
                <span style={styles.currencySymbol}>₹</span>
                <span style={styles.offerPrice}>{plan.offer_price}</span>
                <span style={styles.durationText}>
                  /{selectedDurationMonths === 12
                    ? t(LangKeys.plan_duration_365_days) || "365 Days (1 Year)"
                    : selectedDurationMonths === 6
                    ? t(LangKeys.plan_duration_180_days) || "180 Days (6 Months)"
                    : t(LangKeys.plan_duration_30_days_badge) || "30 Days"}
                </span>
              </div>

              {isExpired ? (
                <div style={styles.cardExpiredText}>
                  {t(LangKeys.expired_on_label)?.replace("{date}", formatExpiryDate(premiumValidTill)) ||
                    `Expired on: ${formatExpiryDate(premiumValidTill)}`}
                </div>
              ) : (
                plan.offer_price !== plan.price && (
                  <div style={styles.oldPrice}>₹{plan.price}</div>
                )
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Plan Details & Feature Grid */}
      {selectedPlan && (
        <div style={styles.detailsCard}>
          <div style={styles.detailsHeader}>
            <span style={styles.detailsTitle}>
              {selectedPlan.name} {t(LangKeys.details) || "Plan Features"}
            </span>
            <span style={styles.secureBadge}>
              🛡️ {t(LangKeys.secure_checkout_label) || "100% Safe SSL Checkout"}
            </span>
          </div>

          <div style={styles.featuresList}>
            {selectedPlan.benefits.map((benefit, i) => (
              <div key={i} style={styles.featureRow}>
                <span style={styles.featureIcon}>{getIconForBenefit(benefit)}</span>
                <span style={styles.featureText}>{getLocalBenefit(benefit)}</span>
              </div>
            ))}
          </div>

          {/* Action Call To Action Button */}
          <button
            onClick={startPayment}
            disabled={paying}
            style={{
              ...styles.checkoutBtn,
              background: isPremium && selectedPlan.id === activePlanId
                ? "linear-gradient(135deg, #D97706, #B45309)"
                : "linear-gradient(135deg, #10B981, #059669)",
            }}
          >
            {paying ? (
              <div style={styles.spinner}></div>
            ) : isPremium && selectedPlan.id === activePlanId ? (
              t(LangKeys.extend_plan_label)?.replace("{days}", currentDurationDays.toString()) ||
              `Extend Plan (+${currentDurationDays} Days)`
            ) : isPremium && selectedPlan.id > activePlanId ? (
              t(LangKeys.upgrade_button) || "Upgrade Plan"
            ) : isPremium && selectedPlan.id < activePlanId ? (
              t(LangKeys.downgrade_button) || "Downgrade Plan"
            ) : (
              t(LangKeys.upgrade_button) || "Get Premium Now"
            )}
          </button>
        </div>
      )}
    </div>
  );

  if (onBack) {
    return (
      <div style={styles.container}>
        <div style={styles.appBar}>
          <button style={styles.backBtn} onClick={onBack}>
            <ArrowLeft size={20} color="#FFFFFF" />
          </button>
          <h3 style={styles.appBarTitle}>{t(LangKeys.premium_plans_title)}</h3>
        </div>
        {content}
      </div>
    );
  }

  return content;
}

const styles = {
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#F8FAFC",
    borderRadius: "24px",
    overflow: "hidden",
  },
  appBar: {
    height: "64px",
    background: "linear-gradient(135deg, #1E293B, #0F172A)",
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    gap: "16px",
    flexShrink: 0,
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
  },
  backBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "4px",
    borderRadius: "8px",
    transition: "background 0.2s",
    outline: "none",
  },
  appBarTitle: {
    color: "#FFFFFF",
    fontSize: "18px",
    fontWeight: "700",
    margin: 0,
    fontFamily: "'Outfit', sans-serif",
  },
  page: {
    padding: "24px",
    flex: 1,
    overflowY: "auto",
    fontFamily: "'Outfit', sans-serif",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  loaderWrap: {
    height: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loader: {
    width: "38px",
    height: "38px",
    border: "3px solid #E2E8F0",
    borderTop: "3px solid #D97706",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  activeBanner: {
    background: "linear-gradient(135deg, #0F172A, #1E293B)",
    borderRadius: "24px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.15), 0 8px 10px -6px rgba(15, 23, 42, 0.15)",
    color: "#FFFFFF",
    position: "relative",
    overflow: "hidden",
  },
  bannerDecor: {
    fontSize: "42px",
    background: "rgba(255, 255, 255, 0.08)",
    padding: "16px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  bannerTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#F8FAFC",
  },
  goldText: {
    color: "#FBBF24",
    background: "rgba(245, 158, 11, 0.15)",
    padding: "4px 10px",
    borderRadius: "10px",
    marginLeft: "6px",
    display: "inline-block",
  },
  bannerSub: {
    fontSize: "14px",
    color: "#94A3B8",
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  daysBadge: {
    color: "#34D399",
    fontWeight: "700",
  },
  introBanner: {
    background: "linear-gradient(135deg, #EEF2F6, #E2E8F0)",
    borderRadius: "24px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    border: "1px solid #CBD5E1",
  },
  introIcon: {
    fontSize: "40px",
    background: "#FFFFFF",
    width: "70px",
    height: "70px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.05)",
  },
  introTitle: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#0F172A",
    margin: 0,
  },
  introDesc: {
    fontSize: "14px",
    color: "#475569",
    margin: "6px 0 0 0",
    lineHeight: "1.5",
  },
  durationSelectorWrap: {
    width: "100%",
  },
  durationSelector: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
    backgroundColor: "#E2E8F0",
    padding: "6px",
    borderRadius: "16px",
  },
  durationTab: {
    border: "none",
    background: "transparent",
    borderRadius: "12px",
    padding: "10px 8px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    transition: "all 0.2s ease",
    outline: "none",
  },
  durationTabActive: {
    backgroundColor: "#FFFFFF",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  },
  durationTabLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748B",
    fontFamily: "'Outfit', sans-serif",
  },
  durationTabLabelActive: {
    fontSize: "14px",
    fontWeight: "800",
    color: "#0F172A",
    fontFamily: "'Outfit', sans-serif",
  },
  durationTabBadge: {
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 8px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
  },
  durationTabBadgeGreen: {
    backgroundColor: "#DCFCE7",
    color: "#15803D",
  },
  durationTabBadgeGold: {
    backgroundColor: "#FEF3C7",
    color: "#B45309",
  },
  plansGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "20px",
    width: "100%",
  },
  planCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    border: "2px solid #E2E8F0",
    padding: "24px",
    cursor: "pointer",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.03)",
    overflow: "hidden",
  },
  selectedCard: {
    borderColor: "#D97706",
    boxShadow: "0 12px 20px -8px rgba(217, 119, 6, 0.25)",
    transform: "translateY(-4px)",
  },
  activeCard: {
    borderColor: "#FBBF24",
    boxShadow: "0 12px 20px -8px rgba(245, 158, 11, 0.25)",
  },
  recommendedBadge: {
    position: "absolute",
    top: "0",
    right: "0",
    backgroundColor: "#DCFCE7",
    color: "#15803D",
    fontSize: "11px",
    fontWeight: "700",
    padding: "6px 14px",
    borderRadius: "0 0 0 16px",
  },
  popularBadge: {
    backgroundColor: "#FEF3C7",
    color: "#B45309",
  },
  activePlanBadge: {
    position: "absolute",
    top: "0",
    right: "0",
    backgroundColor: "#FEF3C7",
    color: "#B45309",
    fontSize: "11px",
    fontWeight: "700",
    padding: "6px 14px",
    borderRadius: "0 0 0 16px",
  },
  planHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
    gap: "10px",
  },
  planName: {
    fontSize: "16px",
    fontWeight: "800",
    color: "#0F172A",
  },
  discountBadge: {
    fontSize: "12px",
    color: "#EF4444",
    backgroundColor: "#FEE2E2",
    padding: "4px 8px",
    borderRadius: "8px",
    fontWeight: "700",
  },
  priceContainer: {
    display: "flex",
    alignItems: "baseline",
    marginTop: "8px",
  },
  currencySymbol: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#0F172A",
    marginRight: "2px",
  },
  offerPrice: {
    fontSize: "36px",
    fontWeight: "900",
    color: "#0F172A",
    letterSpacing: "-1px",
  },
  durationText: {
    fontSize: "13px",
    color: "#64748B",
    marginLeft: "4px",
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: "13px",
    color: "#94A3B8",
    textDecoration: "line-through",
    marginTop: "6px",
    fontWeight: "500",
  },
  detailsCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    border: "1px solid #E2E8F0",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.02)",
  },
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #F1F5F9",
    paddingBottom: "16px",
    flexWrap: "wrap",
    gap: "10px",
  },
  detailsTitle: {
    fontSize: "16px",
    fontWeight: "800",
    color: "#0F172A",
  },
  secureBadge: {
    fontSize: "12px",
    color: "#059669",
    fontWeight: "700",
    backgroundColor: "#D1FAE5",
    padding: "4px 10px",
    borderRadius: "10px",
  },
  featuresList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "14px 24px",
  },
  featureRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  featureIcon: {
    fontSize: "18px",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#F1F5F9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  featureText: {
    fontSize: "14px",
    color: "#334155",
    fontWeight: "500",
    lineHeight: "1.4",
  },
  checkoutBtn: {
    width: "100%",
    height: "54px",
    borderRadius: "16px",
    border: "none",
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.2)",
    transition: "transform 0.2s, opacity 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: "none",
  },
  spinner: {
    width: "24px",
    height: "24px",
    border: "3px solid rgba(255,255,255,0.3)",
    borderTop: "3px solid #ffffff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  expiredBanner: {
    background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)",
    borderRadius: "24px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    border: "1px solid #FCA5A5",
    color: "#991B1B",
    position: "relative",
    overflow: "hidden",
  },
  expiredBannerDecor: {
    fontSize: "42px",
    background: "rgba(220, 38, 38, 0.08)",
    padding: "16px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  expiredText: {
    color: "#EF4444",
    background: "rgba(239, 68, 68, 0.1)",
    padding: "4px 10px",
    borderRadius: "10px",
    marginLeft: "6px",
    display: "inline-block",
    fontWeight: "700",
  },
  expiredOnText: {
    fontSize: "14px",
    color: "#991B1B",
    marginTop: "8px",
    fontWeight: "600",
  },
  cardExpiredText: {
    fontSize: "13px",
    color: "#EF4444",
    marginTop: "6px",
    fontWeight: "700",
  },
  expiredPlanBadge: {
    position: "absolute",
    top: "0",
    right: "0",
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    fontSize: "11px",
    fontWeight: "700",
    padding: "6px 14px",
    borderRadius: "0 0 0 16px",
  },
  expiredCard: {
    borderColor: "#EF4444",
  },
};

export default PremiumPlansPage;
